import { Box, LinearProgress, useMediaQuery } from "@mui/material";
import ExportButton from "components/common/Export";
import { exportURLs } from "components/common/Export/ExportAPIEndpoints";
import { createFilterByType } from "components/common/Filters/Filters.slice";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import { setPageFilter } from "components/common/Filters/GlobalFilters.slice";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { cloneDeep, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { planSourceList } from "utils/CommonConstants";
import {
  convertArrayToString,
  findDisplayNameById,
  formFilterRequestPayload,
  functionalAreaIdToName,
  functionalAreaIdsToNames,
  getLoggedInUserDetails,
  getMembersList,
  getMoreValues,
  getNamesFromHtlId
} from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import { getPlanDetailsRoute } from "utils/StoreUtils";
import { serviceTypes } from "utils/Types";
import { theme as MUITheme } from "../../../../styles/Theme";
import {
  fetchPropertyPlans,
  fetchPropertySnapshots,
  fetchUserDetails,
  reset,
  setPropertyList,
  setPropertyTotalCount,
  setSnapshots
} from "../IndividualProperty.slice";
import { propertyPlansHeader } from "../TableHeadersConfig";
import "./index.scss";

const PropertyPlans = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const translate = useTranslate();
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const { isLeadership } = useAppSelector((state) => state.app.loggedInUserDetails);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const propertyPlans = useAppSelector((state) => state.individualProperty.propertyPlans);
  const sorting = useAppSelector((state) => state.sortingDetails.sorting);
  const innCode = useAppSelector((state: any) => state.app.innCode);

  const globalFiltersforPage = useAppSelector(
    (state) => state.globalFilters[filterPage.individualPropertyPlansDisplay]
  );
  const searchText = globalFiltersforPage.searchText;

  const filterRequestPayload =
    (isEmpty(globalFiltersforPage?.modifiedFilter)
      ? globalFiltersforPage?.defaultFilter
      : globalFiltersforPage?.modifiedFilter) || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);

  const [page, setPage] = useState(0);
  const [userData, setUserData] = useState([]);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [isComponentLoad, setIsComponentLoad] = useState<boolean>(true);

  const loggedInUserDetail = getLoggedInUserDetails();
  const recentSortingApplied = sorting?.["propertyPlan"];

  const getMetricsDataForCard = (data: any) => {
    return [
      { isPrimary: true, subTitle: "*Completed", data: data?.completedLast90Days },
      { subTitle: "Total Active", data: data?.active, showBorder: true },
      { subTitle: "Overdue", data: data?.overdue }
    ];
  };

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, []);

  const apiCall = async (pageArgs?: number | undefined) => {
    const clonesPayload = cloneDeep(formattedFilterRequestPayload);
    const rest = convertArrayToString(clonesPayload);
    const selectedPage = pageArgs ?? page;
    const dataPayload = {
      page: pageArgs ?? page,
      payload: {
        ...(recentSortingApplied && Object.keys(recentSortingApplied).length > 0
          ? { sort: { ...recentSortingApplied } }
          : {}),
        innCode: params.inncode || innCode,
        includeOwners: page === 0,
        searchText,
        fetchOwnersByInncodes: true,
        ...rest
      }
    };

    const res = await dispatch(fetchPropertyPlans(dataPayload));
    const payload = res.payload?.userplans;
    const owners = res?.payload?.meta?.owners;
    const totalCount = res.payload?.meta?.page?.total;
    let data: any;
    if (selectedPage === 0) {
      data = payload;
    } else {
      data = [...propertyPlans.list, ...payload];
    }
    if (data && owners.length && selectedPage === 0) {
      const idPayload: any = {
        value: "",
        hltIds: owners,
        includecorpusers: false,
        userType: "",
        page: 0,
        size: owners?.length ?? 0
      };
      const res = await dispatch(fetchUserDetails(idPayload));
      const userList = res?.payload?.users;
      setUserData(userList);
    }
    dispatch(setPropertyTotalCount(totalCount));
    dispatch(setPropertyList(data));
  };

  useEffect(() => {
    (async () => {
      if (page > 0) {
        apiCall();
      }
      if (isComponentLoad) setIsComponentLoad(false);
    })();
  }, [page]);

  useEffect(() => {
    setPage(0);
    if (isMount) apiCall(0);
    setIsMount(true);
  }, [filterRequestPayload, recentSortingApplied, innCode, searchText, isMount]);

  useEffect(() => {
    (async () => {
      if (isMount) {
        const rest = convertArrayToString(formattedFilterRequestPayload);
        const payloadData = {
          innCode: params.inncode || innCode,
          ...rest
        };

        const res = await dispatch(fetchPropertySnapshots(payloadData));

        const payload: any = {
          cardSnapshot: res.payload.PROPERTY_CARDS_SNAPSHOT,
          planSnapshpt: res.payload.PROPERTY_PLAN_SNAPSHOT
        };
        dispatch(setSnapshots(payload));
      }
    })();
  }, [filterRequestPayload, isMount, innCode]);

  const handleSetPage = (pageNo: number) => {
    setPage(pageNo);
  };

  const getDateColor = (date: any, status: string) => {
    const todaysDate = moment().format("YYYY-MM-DD");
    return moment(date).isBefore(todaysDate) && status !== "Completed"
      ? tokens.Text.Feedback.Alert
      : tokens.Text.General.PrimaryHeadline;
  };

  const updateSavedPropertyPlan = async (isReset = false) => {
    const filterPayload = isReset ? [] : filterRequestPayload;
    const dataPayload = {
      other: [
        {
          page: filterPage.individualPropertyPlansDisplay,
          filters: filterPayload
        }
      ]
    };
    await dispatch(createFilterByType(dataPayload));

    dispatch(
      setPageFilter({
        pageName: filterPage.individualPropertyPlansDisplay,
        defaultFilter: filterPayload,
        modifiedFilter: []
      })
    );
  };

  return (
    <div className="property-plan-component">
      <div className="filter-wrapper">
        <GlobalFilters
          isClearAllDefault={isLeadership || false}
          innCode={params?.inncode || innCode}
          page={filterPage.individualPropertyPlansDisplay}
          isDefaultButtonsApplicable={loggedInUserDetail?.roleCategory === "Leadership"}
          updateSavedFilter={updateSavedPropertyPlan}
          ownerDetails={userData}
          isSearchBarVisible
          isPreDefinedNav={true}
          searchedText={searchText}
          getMembersList={(searchValue: string) => getMembersList(searchValue, dispatch, params.inncode || innCode)}
        />
      </div>

      {!isComponentLoad && (
        <div className="content-wrapper" id="propertiesDiv">
          <div className="filter-desc-wrapper">
            <GlobalFilterDescription
              page={filterPage.individualPropertyPlansDisplay}
              bgColor={tokens.Text.General.Inverse}
            />
          </div>
          <div className="key-metrics-wrapper">
            <KeyMetrics
              label={translate("dashboard.PlanLabel")}
              keyMetricsData={getMetricsDataForCard(propertyPlans?.snapshot?.PROPERTY_PLAN_SNAPSHOT?.[0])}
              helperText="*Last 90 Days"
            />
            <KeyMetrics
              label="Actions"
              keyMetricsData={getMetricsDataForCard(propertyPlans?.snapshot?.PROPERTY_CARDS_SNAPSHOT?.[0])}
            />
          </div>
          <div className="table-wrapper">
            <div className="action-wrapper-table">
              <div>
                <ExportButton
                  URL={exportURLs.individualProperty_plans}
                  data={{
                    innCode: params.inncode || innCode,
                    ...convertArrayToString(formattedFilterRequestPayload)
                  }}
                  serviceType={serviceTypes.userplan}
                />
              </div>
            </div>

            <div>
              <BasicTable
                scrollableTarget="propertiesDiv"
                tableName="propertyPlan"
                totalRecords={propertyPlans.totalRecords}
                pagination={{ pageNo: page, setPageNo: handleSetPage }}
                data={{
                  headers: propertyPlansHeader,
                  rows: propertyPlans?.list?.map((plans: any) => {
                    const progressVal = plans?.cardsCompleted / plans?.totalCards;
                    const percent = (!isNaN(progressVal) ? progressVal : 0) * 100;
                    return {
                      data: [
                        {
                          content: (
                            <Box
                              sx={{
                                textDecoration: "none",
                                cursor: "pointer",
                                color: tokens.Text.General.PrimaryHeadline,
                                fontWeight: "700"
                              }}
                              onClick={() => {
                                const route = getPlanDetailsRoute();
                                navigate(`/${route}/${plans?.id}`);
                              }}
                            >
                              {plans.title}
                            </Box>
                          ),
                          showBold: true
                        },
                        {
                          content: getMoreValues(getNamesFromHtlId(plans.owners, userData)),
                          tooltip: {
                            title: "Owners",
                            list: plans.owners?.map((hltId: any) => {
                              return { name: findDisplayNameById(hltId, userData) };
                            })
                          }
                        },
                        {
                          content: planSourceList[plans.planSource]
                        },
                        {
                          content: getMoreValues(functionalAreaIdsToNames(plans.functionalAreaIds)),
                          tooltip: {
                            title: "Functional Areas",
                            list: plans?.functionalAreaIds?.map((FA: any) => {
                              return { name: functionalAreaIdToName(FA, functionalAreaList) };
                            })
                          }
                        },
                        {
                          content: plans.priority
                        },
                        {
                          content: `${getUIFormatDate(plans?.dueDate)}` || "-",
                          color: getDateColor(plans?.dueDate, plans?.status)
                        },
                        {
                          content: (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <Typography variant="label" showBold color="#4D4745" className="no-wrap-white-space">
                                {plans.status}
                              </Typography>
                              <LinearProgress
                                sx={{ width: "64px", height: "6px" }}
                                variant="determinate"
                                value={percent}
                              />
                              <div>
                                <Typography variant="label" color={tokens.Border.General.Secondary}>
                                  {`${plans?.cardsCompleted}/${plans?.totalCards}`} Done
                                </Typography>
                              </div>
                            </div>
                          )
                        }
                      ]
                    };
                  })
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyPlans;
